/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";

import { memo } from "react";
import { useForm, useWatch, FormProvider } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";

// 入力フォームのstore保存
import { useStateMachine } from "little-state-machine";
import { getStore, contractAction, mtgCallbackUpdateAction } from "stores/actions.js";

// JS読み込み
import {
  formatYubinNo,
} from "utilities/formater";

// ui
import NextButton from "components/ui/button/NextButton";
import BackButton from "components/ui/button/BackButton";
import WhiteButton from "components/ui/button/WhiteButton";
import InputTextArea from "components/ui/input/InputTextArea";
import CheckBox from "components/ui/checkbox/CheckBox";

// Parts
import H3 from "components/parts/title/H3";
import MailAddressAgain from "components/parts/MailAddressAgain";
import NameKj from "components/parts/NameKj";
import NameKn from "components/parts/NameKn";
import PhoneNo from "components/parts/PhoneNo";
import TelNo from "components/parts/TelNo";
import BantGo from "components/parts/BantGo";
import BldNmKj from "components/parts/BldNmKj";
import MunebanHeyaban from "components/parts/MunebanHeyaban";
import MtgLoginSelect from "components/parts/MtgLoginSelect";

// CSS読み込み
import { mtgGroup, mtgBody, mtgBodyNoMb, mtgLabel, mtgWhiteBackground } from "assets/css/stylesCss";
import Section from "components/ui/section/Section";
import { useEffect } from "react";
import { telKbnCheck } from "utilities/string";

/**
 * お客さま情報
 */
const Contract = memo(() => {

  // 画面遷移用
  const navigate = useNavigate();

  // store取得・更新
  const { state } = useStateMachine(getStore);
  const { memberInfoList } = state.mtgCallback;
  const { actions } = useStateMachine({
    contractAction,
    mtgCallbackUpdateAction,
  });

  // MTGログイン画面のStoreデータ削除
  const clearMtgCbStore = () =>
    actions.mtgCallbackUpdateAction({
      memberInfoList: "",
    });

  // react-hook-formの設定
  const methods = useForm({
    defaultValues: {
      isActive: true,
      mtgSelect: state.storeContract.mtgSelect,
      mailaddress: state.storeContract.mailaddress,
      mailaddressAgain: state.storeContract.mailaddressAgain,
      mskmSei: state.storeContract.mskmSei,
      mskmMei: state.storeContract.mskmMei,
      mskmSeiKn: state.storeContract.mskmSeiKn,
      mskmMeiKn: state.storeContract.mskmMeiKn,
      phoneNo: state.storeContract.phoneNo,
      telNo: state.storeContract.telNo,
      bant: state.storeContract.bant,
      go: state.storeContract.go,
      bldNmKj: state.storeContract.bldNmKj,
      muneban: state.storeContract.muneban,
      heyaban: state.storeContract.heyaban,
      contact: state.storeContract.contact,
      guidanceAgree: state.storeContract.guidanceAgree,
      mtgLoginSelect: state.storeContract.mtgLoginSelect,
    },
    mode: "onSubmit",
    reValidateMode: "onSubmit",
  });

  const {
    control,
    formState: { isValid },
    setValue,
    getValues,
    clearErrors,
  } = methods;

  // MTG連携ボタンクリック時
  const handleOnClickMTGLogin = () => {

    // MTGログイン画面のStoreデータ削除
    clearMtgCbStore();

    // stateを発番する
    const state = uuidv4().replaceAll("-", "");

    // sessionstorageへ一旦state保持
    sessionStorage.setItem("oidcState", state);

    const authUri = process.env.REACT_APP_IDCS_AUTHORIZATION_URI;
    const responseType = process.env.REACT_APP_IDCS_RESPONSE_TYPE;
    const scope = process.env.REACT_APP_IDCS_SCOPE;
    const clientId = process.env.REACT_APP_IDCS_CLIENT_ID;
    const redirectUri = process.env.REACT_APP_IDCS_REDIRECT_URI_TEMPLATE;

    // MTGログイン画面遷移
    window.location.href = `${authUri}?response_type=${responseType}&scope=${scope}&client_id=${clientId}&redirect_uri=${redirectUri}&state=${state}`;
  };

  // 次へボタン押下時
  const onSubmit = (data) => {
    // myTOKYOGAS選択状態をstateに追加
    data.mtgLoginSelect = watchMtgSelect;
    actions.contractAction(data);
    // 確認画面へ遷移
    navigate("/confirm", {
      state: { isSubmit: true },
    });
  };

  // myTOKYOGASの選択状態を監視
  const watchMtgSelect = useWatch({
    control,
    name: "mtgLoginSelect",
    defaultValue: state.storeContract.mtgLoginSelect, // 初期値はstateに保存してある値
  });

  // myTOKYOGAS情報ラジオボタン選択値変更時
  useEffect(() => {

    // 入力チェックを実施
    let isShouldValidate = false;

    // myTOKYOGAS連携をしていない場合は処理終了
    if (!memberInfoList.length > 0) return;

    // エラー状態をクリア
    clearErrors(["mailaddress", "mailaddressAgain", "mskmSei", "mskmMei", "mskmSeiKn", "mskmMeiKn", "phoneNo", "telNo", "bant", "go", "bldNmKj", "muneban", "heyaban"])

    // 選択値とstateに保持している値に差異がない場合はstateの保存値を設定し、myTOKYOGASの情報は使用しない
    if (watchMtgSelect === state.storeContract.mtgLoginSelect) {
      setValue("mailaddress", state.storeContract.mailaddress, { shouldValidate: isShouldValidate, });
      setValue("mailaddressAgain", state.storeContract.mailaddress, { shouldValidate: isShouldValidate, });
      setValue("mskmSei", state.storeContract.mskmSei, { shouldValidate: isShouldValidate, });
      setValue("mskmMei", state.storeContract.mskmMei, { shouldValidate: isShouldValidate, });
      setValue("mskmSeiKn", state.storeContract.mskmSeiKn, { shouldValidate: isShouldValidate, });
      setValue("mskmMeiKn", state.storeContract.mskmMeiKn, { shouldValidate: isShouldValidate, });
      setValue("phoneNo", state.storeContract.phoneNo, { shouldValidate: isShouldValidate, });
      setValue("telNo", state.storeContract.telNo, { shouldValidate: isShouldValidate, });
      setValue("bant", state.storeContract.bant, { shouldValidate: isShouldValidate, });
      setValue("go", state.storeContract.go, { shouldValidate: isShouldValidate, });
      setValue("bldNmKj", state.storeContract.bldNmKj, { shouldValidate: isShouldValidate, });
      setValue("muneban", state.storeContract.muneban, { shouldValidate: isShouldValidate, });
      setValue("heyaban", state.storeContract.heyaban, { shouldValidate: isShouldValidate, });
      return;
    };

    // myTOKYOGASから連携された情報を選択時
    if (watchMtgSelect !== "99") {
      // 選択ラジオボタンのvalue値
      const selectValue = Number(watchMtgSelect);

      setValue("mailaddress", memberInfoList[selectValue].mailAddress, { shouldValidate: isShouldValidate, });
      setValue("mailaddressAgain", memberInfoList[selectValue].mailAddress, { shouldValidate: isShouldValidate, });
      setValue("mskmSei", memberInfoList[selectValue].sei, { shouldValidate: isShouldValidate, });
      setValue("mskmMei", memberInfoList[selectValue].mei, { shouldValidate: isShouldValidate, });
      setValue("mskmSeiKn", memberInfoList[selectValue].seiKn, { shouldValidate: isShouldValidate, });
      setValue("mskmMeiKn", memberInfoList[selectValue].meiKn, { shouldValidate: isShouldValidate, });
      setValue("phoneNo", telKbnCheck(memberInfoList[selectValue].telNo, "1"), { shouldValidate: isShouldValidate, });
      setValue("telNo", telKbnCheck(memberInfoList[selectValue].telNo, "0"), { shouldValidate: isShouldValidate, });

      // 「ご希望日時」で入力した住所コード（郵便番号）と同じ住所コードのmTG情報を選択した場合のみmTG情報から補完
      if (memberInfoList[selectValue].addressCd === state.storeAppointment.addressKj) {
        setValue("bant", memberInfoList[selectValue].bant, { shouldValidate: isShouldValidate, });
        setValue("go", memberInfoList[selectValue].go, { shouldValidate: isShouldValidate, });
        setValue("bldNmKj", memberInfoList[selectValue].bldName, { shouldValidate: isShouldValidate, });
        setValue("muneban", memberInfoList[selectValue].muneban, { shouldValidate: isShouldValidate, });
        setValue("heyaban", memberInfoList[selectValue].heyaban, { shouldValidate: isShouldValidate, });
      } else {
        setValue("bant", "", { shouldValidate: isShouldValidate, });
        setValue("go", "", { shouldValidate: isShouldValidate, });
        setValue("bldNmKj", "", { shouldValidate: isShouldValidate, });
        setValue("muneban", "", { shouldValidate: isShouldValidate, });
        setValue("heyaban", "", { shouldValidate: isShouldValidate, });
      }

    }
    // 「myTOKYOGASの情報は使わない」選択時
    if (watchMtgSelect === "99") {
      setValue("mailaddress", "", { shouldValidate: isShouldValidate, });
      setValue("mailaddressAgain", "", { shouldValidate: isShouldValidate, });
      setValue("mskmSei", "", { shouldValidate: isShouldValidate, });
      setValue("mskmMei", "", { shouldValidate: isShouldValidate, });
      setValue("mskmSeiKn", "", { shouldValidate: isShouldValidate, });
      setValue("mskmMeiKn", "", { shouldValidate: isShouldValidate, });
      setValue("phoneNo", "", { shouldValidate: isShouldValidate, });
      setValue("telNo", "", { shouldValidate: isShouldValidate, });
      setValue("bant", "", { shouldValidate: isShouldValidate, });
      setValue("go", "", { shouldValidate: isShouldValidate, });
      setValue("bldNmKj", "", { shouldValidate: isShouldValidate, });
      setValue("muneban", "", { shouldValidate: isShouldValidate, });
      setValue("heyaban", "", { shouldValidate: isShouldValidate, });
    }
  }, [watchMtgSelect]);

  return (
    <>
      <h1>お客さま情報入力</h1>
      <FormProvider {...methods}>

        {/***************** お客さま情報 ******************/}
        <H3 title="お客さま情報" required={true}></H3>

        {/** myTOKYOGASログインボタン */}
        {memberInfoList === "" && (
          <>
            <div css={mtgGroup}>
              {/** MEMO:通常のmtgBodyとmarginBottomが違うため直書き */}
              <p css={mtgBody} style={{ marginBottom: '12px' }}>
                myTOKYOGASアカウントをお持ちの方は、ログインすることでお客さま情報を簡単に入力できます。
              </p>
              <form>
                <WhiteButton
                  value="myTOKYOGASから自動入力"
                  isActive={true}
                  fn={() => handleOnClickMTGLogin()}
                >myTOKYOGASから自動入力</WhiteButton>
              </form>
            </div>
          </>
        )}

        {/** myTOKYOGAS一覧 */}
        {memberInfoList.length > 0 && (
          <>
            <div css={mtgGroup}>
              {/* MTGログイン契約情報 */}
              <MtgLoginSelect
                mtgItems={memberInfoList}
                mtgSelfInput={true}
              />
            </div>
          </>
        )}

        {/* お申込み条件確認 */}
        <form onSubmit={methods.handleSubmit(onSubmit)}>

          {/***************** お客さま情報 入力部 *****************/}
          <Section>
            {/** メールアドレス */}
            <MailAddressAgain
              label="メールアドレス"
              required={true}
              name="mailaddress"
              maxLength="256"
              txtCaption="半角英数字で入力してください。"
            ></MailAddressAgain>

            {/** お名前（漢字） */}
            <NameKj
              sei="mskmSei"
              mei="mskmMei"
              required={true}
              maxLength="10"
              txtCaption="合計9文字以内の全角で入力してください。"
            ></NameKj>

            {/** お名前（カタカナ） */}
            <NameKn
              seiKn="mskmSeiKn"
              meiKn="mskmMeiKn"
              required={true}
              maxLength="20"
              txtCaption="合計19文字以内の全角で入力してください。"
            ></NameKn>

            {/** 携帯電話番号 */}
            <PhoneNo
              required={false}
              maxLength="13"
              message="訪問前に到着時間などをご連絡いたします。"
              txtCaption="半角数字のみで入力してください。"
            ></PhoneNo>

            {/** 固定番号 */}
            <TelNo
              required={false}
              maxLength="13"
              txtCaption={
                [
                  "携帯電話番号または固定電話番号を入力してください。",
                  "半角数字のみで入力してください。"
                ]
              }
            ></TelNo>
          </Section>

          {/***************** 設置・交換作業場所 *****************/}
          <Section>
            <H3 title="設置・交換作業場所" required={true} marginBottom="24px"></H3>
            {/** 訪問エリア */}
            <div css={mtgGroup}>
              <label css={mtgLabel}>
                訪問エリア
              </label>
              <div css={mtgWhiteBackground}>
                <p css={mtgBodyNoMb}>
                  {formatYubinNo(state.storeAppointment.yubinNo, true)}
                  <br />
                  {state.storeAppointment.addressKjLabel}
                </p>
              </div>
            </div>

            {/** 番地・号 */}
            <BantGo
              required={true}
              txtCaption={
                [
                  "枝番がある場合、号の欄に - (ハイフン)で区切って入力してください。",
                  "半角数字で入力してください。"
                ]
              }
            ></BantGo>

            {/** 建物名 */}
            <BldNmKj
              required={true}
              maxLength="15"
              txtCaption="長い名称の場合は15文字目までを全角で入力してください。"
            ></BldNmKj>

            {/** 棟番・部屋番 */}
            <MunebanHeyaban
              required={true}
              txtCaption="半角数字・アルファベットで入力してください。"
            ></MunebanHeyaban>
          </Section>

          {/***************** ご連絡事項など *****************/}
          <Section>
            <H3 title="ご連絡事項など" required={false} marginBottom="12px"></H3>
            <div css={mtgGroup}>
              {/** MEMO:通常のmtgBodyとmarginBottomが違うため直書き */}
              <div css={mtgBody} style={{ marginBottom: "12px" }}>
                作業立会いの方の連絡先が異なる場合や、駐車場所の指定などがある場合はご記入ください。
              </div>
              <InputTextArea
                name="contact"
                maxLength="500"
                placeholder="【ご記入例】&#13;当日の立会者の連絡先は、090-1234-5678です。"
              ></InputTextArea>
            </div>
          </Section>

          {/***************** メールでのご案内について *****************/}
          <Section>
            <H3 title="メールでのご案内について" required={false} marginBottom="12px"></H3>
            <div css={mtgGroup}>
              <div>
                {/** MEMO:通常のmtgBodyとmarginBottomが違うため直書き */}
                <div css={mtgBody} style={{ marginBottom: "12px" }}>
                  今後、東京ガスの各種サービスに関するおトク情報やお役立ち情報などをメールにてお届けしてもよろしいですか？
                </div>
                <CheckBox
                  id="guidanceAgree"
                  name="guidanceAgree"
                  value={true}
                  rules={{
                    required: {
                      value: false,
                      message:
                        "サービス内容について確認のうえチェックしてください。",
                    },
                  }}
                >
                  はい
                </CheckBox>
              </div>
            </div>
          </Section>

          {/***************** 確認ボタン *****************/}
          <NextButton
            isActive={getValues("isActive")}
            value="確認"
          ></NextButton>

        </form>
        {/***************** 戻るボタン *****************/}
        <BackButton url={"/KikiSelect"} />
      </FormProvider>
    </>
  );
});

export default Contract;